import { useParams } from "react-router-dom";
import { MyProjectAppBar } from "./myProjectAppBar";
import { ProjectCanvas } from "./projectCanvas";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { doc, getDoc, updateDoc, addDoc, collection, getDocs, query, deleteDoc } from 'firebase/firestore';
import { db, storage } from '../../config/firebase';
import React, { useState, useRef } from "react";
import { ProjectContext } from "../../contexts/ProjectContext";
import { ThemeProvider, useMediaQuery } from "@mui/material";
import { DeleteElementDialog } from "../Dialogs/deleteElementDialog";
import ProjectDrawer from "./Drawer/projectDrawer";
import { ProjectData } from "../../data/projectData";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import defaultTheme from '../Themes/defaultTheme';
import { useNavigate } from "react-router-dom";
import { LoadingCircle } from "../loading";
import { ElementData } from "../../data/elementData";
import { ref, deleteObject } from "firebase/storage";
import { ElementInfo } from "./ElementProperties/elementInfo";
import { ElementPropertiesDialog } from "./ElementProperties/elementPropertiesDialog";
import { importElementsFromJson as importElementsFromJsonService } from "../../services/elementImportService";
import { ProfilMenuInfo } from "../profilMenuInfo";
import { Adsense } from "@ctrl/react-adsense";
// import { ProjectSpeeddial } from "./projectSpeeddial";
// import { HelpSpeeddial } from "./helpSpeeddial";
// import { QuickActions } from "./quickActions";

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// Responsive helper hook
function useIsMobile(breakpoint = 768) {
    // Use Material-UI's useMediaQuery for reliable responsive detection
    const isMobileQuery = useMediaQuery(`(max-width:${breakpoint}px)`, { noSsr: true });
    const [isMobile, setIsMobile] = React.useState(false);
    const isInitialMount = React.useRef(true);

    React.useEffect(() => {
        // On initial mount, check window width directly
        // This is critical for mobile devices where viewport might not be fully initialized
        if (isInitialMount.current && typeof window !== 'undefined') {
            setIsMobile(window.innerWidth <= breakpoint);
            isInitialMount.current = false;

            // Also check after a brief delay to catch any viewport initialization
            const timeoutId = setTimeout(() => {
                if (typeof window !== 'undefined') {
                    setIsMobile(window.innerWidth <= breakpoint);
                }
            }, 100);

            return () => clearTimeout(timeoutId);
        } else {
            // After initial mount, use useMediaQuery for reactive updates
            setIsMobile(isMobileQuery);
        }
    }, [isMobileQuery, breakpoint]);

    return isMobile;
}

export const ProjectComponent = (props) => {
    // --- States ---
    const [elements, setElements] = useState([]);
    const [searchElements, setSearchElements] = useState([]);
    const [projectRef, setProjectRef] = useState();
    const [selectedItem, setSelectedItem] = useState();
    const [selectedItemId, setSelectedItemId] = useState(-1);
    const [selectedItemType, setSelectedItemType] = useState("");
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showProjectDrawer, setShowProjectDrawer] = useState(false);
    const [showElementProperties, setShowElementProperties] = useState(false);
    const [projectIsFullyLoaded, setProjectIsFullyLoaded] = useState(false);
    const [notificationOpen, setNotificationOpen] = useState({ state: false, sev: "success", msg: "N/A" });
    const params = useParams();
    const canvasRef = useRef(null);
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [projectId, setProjectId] = useState("");
    const isMobile = useIsMobile();

    // --- Queries ---
    const { data: projectData, isLoading, isError } = useQuery({
        queryKey: ["project", params.id],
        queryFn: async () => {
            try {
                setProjectId(params.id);
                const docRef = doc(db, "project", params.id);
                const docSnap = await getDoc(docRef)
                setProjectRef(docRef);
                if (docSnap.exists()) {
                    if (!docSnap.data().ispublic && !props.user) navigate("/login");
                    return docSnap.data();
                } else {
                    console.log("Error: Can't load project");
                }
            } catch (e) {
                console.error("Failed to load project.", e);
            }
        },
        cacheTime: 10 * 60 * 1000,
        retry: 2,
    });

    const { isLoading: isProjectNodesLoading, isError: isProjectNodesError } = useQuery({
        queryKey: ["nodes", projectId],
        queryFn: async () => {
            try {
                const q = query(collection(db, "project", projectId, "elements"));
                const data = await getDocs(q)
                const filteredData = data.docs.map((doc) => {
                    const elementData = doc.data();
                    if (elementData.style && elementData.style["background-image"] && !elementData.data?.image) {
                        elementData.data = {
                            ...elementData.data,
                            image: elementData.style["background-image"]
                        };
                    }
                    return {
                        ...elementData,
                        data: {
                            ...elementData.data,
                            id: doc.id
                        }
                    };
                });
                const searchableElements = filteredData.filter(element => element.group === "nodes");
                setSearchElements(searchableElements);
                setElements([...filteredData]);
                setProjectIsFullyLoaded(true);
                return filteredData;
            } catch (e) {
                console.error("Failed to load project nodes.", e);
            }
        },
        enabled: !!projectData,
    });

    const { data: userPublicData } = useQuery({
        queryKey: ["userPublicData", projectId],
        queryFn: async () => {
            try {
                const docRef = doc(db, "user", projectData.owner);
                const docSnap = await getDoc(docRef)
                if (docSnap.exists()) {
                    return docSnap.data();
                } else {
                    console.log("Error: Can't load project owner public data");
                }
            } catch (e) {
                console.error("Failed to load owner public data.", e);
            }
        },
        enabled: !!projectData
    });

    // --- Logic for manipulating elements is unchanged from previous ---

    const elementsRef = useRef(elements);
    React.useEffect(() => {
        elementsRef.current = elements;
    }, [elements]);

    const saveProject = () => {
        updateDoc(projectRef, projectData)
            .then(() => { })
            .catch(error => {
                setNotificationOpen({ state: true, sev: "error", msg: "Project saving failed!" })
            });
    }

    const getElements = (data) => {
        const loadedElements = [];
        if (!data.elements) {
            return [...loadedElements];
        }

        setSearchElements(data.elements);
        setElements([...data.elements]);
        return [...data.elements];
    }

    const addConnection = (sourceNodeId, targetNodeId, type, save) => {
        let newConnection = {
            group: "edges",
            data: {
                name: "Edge",
                edgetype: projectData.defaultedgetype,
                edgearrows: projectData.edgearrows
            }
        };

        if (type.toUpperCase() === "PARENT") {
            newConnection.data.source = sourceNodeId;
            newConnection.data.target = targetNodeId;
        }
        if (type.toUpperCase() === "CHILD") {
            newConnection.data.source = targetNodeId;
            newConnection.data.target = sourceNodeId;
        }

        addNodeData(newConnection);

        return newConnection;
    }

    const addNodeData = async (data) => {
        data.data = { ...(data.data || {}) };
        if (!data.data.createdon) {
            data.data.createdon = new Date().toISOString();
        }
        const newElementRef = collection(db, 'project', projectId, 'elements');
        const res = await addDoc(newElementRef, data);
        data.data.id = res.id;
        var newnodes = [];
        newnodes.push(data);
        let updatedElements;
        setElements(prevElements => {
            updatedElements = [...(prevElements || []), ...newnodes];
            return updatedElements;
        });
        if (data.group === "nodes") {
            setSearchElements(prevSearchElements => [...prevSearchElements, data]);
        }
        await new Promise(resolve => setTimeout(resolve, 0));
        elementsRef.current = updatedElements;
        return res.id;
    }

    const addNode = async (data, type) => {
        if (data.hasOwnProperty('id')) delete data.id;
        const newNode = {
            group: "nodes",
            data: {
                createdon: new Date().toUTCString(),
                ...data
            }
        }

        newNode.data.nodeshape = projectData.defaultshapetype;

        const newId = await addNodeData(newNode);

        if (type.toUpperCase() !== "O") {
            addConnection(selectedItemId, newId, type, false);
        }
        await new Promise(resolve => setTimeout(resolve, 0));
        return newId;
    }

    const addComment = async (data) => {
        const newCommentRef = collection(db, 'project', projectId, 'comments');
        await addDoc(newCommentRef, data);
        // Invalidate and refetch comments query to refresh the list
        queryClient.invalidateQueries({ queryKey: ["loadmycomments", projectId] });
    }

    const deleteElementDialog = (id) => {
        setShowDeleteDialog(true);
    }

    const closeDeleteElementDialog = () => {
        setShowDeleteDialog(false);
    }

    const deletePicture = (picUrl) => {
        const desertRef = ref(storage, picUrl);
        deleteObject(desertRef).then(() => {
            console.log("Picture deleted");
        }).catch((error) => {
            console.log("Picture NOT deleted!");
        });
    }

    const deleteElement = async () => {
        if (selectedItemType === 'nodes') {
            if (selectedItem.data.image && selectedItem.data.image !== "") {
                let picUrl = `projects/${projectData.projectId}/${selectedItem.data.id}.jpeg`;
                deletePicture(picUrl)
            }
        }

        await deleteDoc(doc(db, "project", projectId, "elements", selectedItemId));

        const elementsToDelete = elements.filter(el =>
            el.data.source === selectedItemId ||
            el.data.target === selectedItemId
        );

        for (const el of elementsToDelete) {
            if (el.data.id !== selectedItemId) {
                await deleteDoc(doc(db, "project", projectId, "elements", el.data.id));
            }
        }

        setElements(current => current.filter(el => {
            return el.data.id !== selectedItemId && el.data.source !== selectedItemId && el.data.target !== selectedItemId
        }));

        setSearchElements(current => current.filter(el => {
            return el.data.id !== selectedItemId && el.data.source !== selectedItemId && el.data.target !== selectedItemId
        }));
        SelectItem("x");
        saveProject();
    }

    const deleteElementById = async (id) => {
        let node = elements.filter((el) => { return el.data.id == id; });

        if (node.length !== 0) {
            let a = node[0];
            a = {
                ...node[0],
                data: { ...node[0].data }
            }

            if (a.group === 'nodes') {
                if (a.data.image && a.data.image !== "") {
                    let picUrl = `projects/${projectData.projectId}/${a.data.id}.jpeg`;
                    deletePicture(picUrl)
                }
            }
            await deleteDoc(doc(db, "project", projectId, "elements", a.data.id));
            const elementsToDelete = elements.filter(el =>
                el.data.source === a.data.id ||
                el.data.target === a.data.id
            );

            for (const el of elementsToDelete) {
                if (el.data.id !== a.data.id) {
                    await deleteDoc(doc(db, "project", projectId, "elements", el.data.id));
                }
            }

            setElements(current => current.filter(el => {
                return el.data.id !== a.data.id && el.data.source !== a.data.id && el.data.target !== a.data.id
            }));

            setSearchElements(current => current.filter(el => {
                return el.data.id !== a.data.id && el.data.source !== a.data.id && el.data.target !== a.data.id
            }));
        }
    }

    const deleteElementByIds = async (ids) => {
        let node = elements.filter((el) => { return ids.includes(el.data.id); });

        if (node.length !== 0) {
            node.forEach(a => {
                deleteElementById(a.data.id);
            });
        }
    }

    const updateElement = (data) => {
        const updatedElements = elements.map(el => {
            if (el.data.id === data.data.id) {
                return {
                    ...el,
                    ...data
                };
            } else {
                return el;
            }
        });
        setElements(updatedElements);
        const docRef = doc(db, "project", projectId, "elements", data.data.id);
        updateDoc(docRef, data)
            .then(() => {
            })
            .catch(error => {
                setNotificationOpen({ state: true, sev: "error", msg: "Element saving failed!" })
            });
    }

    const updateElementImage = (url) => {
        const newData = {
            data: {
                image: url
            }
        };

        const updatedElements = elements.map(el => {
            if (el.data.id === selectedItem.data.id) {
                return {
                    ...el,
                    ...newData,
                    data: {
                        ...el.data,
                        ...newData.data
                    }
                };
            } else {
                return el;
            }
        });

        setElements(updatedElements);

        const updatedSelectedItem = updatedElements.find(el => el.data.id === selectedItem.data.id);
        if (updatedSelectedItem) {
            setSelectedItem(updatedSelectedItem);
        }

        const docRef = doc(db, "project", projectId, "elements", selectedItem.data.id);
        const firestoreData = {
            style: newData.style,
            data: {
                ...selectedItem.data,
                ...newData.data
            }
        };
        updateDoc(docRef, firestoreData)
            .then(() => {
            })
            .catch(error => {
                setNotificationOpen({ state: true, sev: "error", msg: "Element saving failed!" })
            });
    }

    const updateProject = (data) => {
        if (!data) return;
        projectData.name = data.name;
        projectData.description = data.description;
        projectData.layout = data.layout;

        if (data.backgroundcolor !== null && data.backgroundcolor !== undefined)
            projectData.backgroundcolor = data.backgroundcolor
        else
            projectData.backgroundcolor = ProjectData.backgroundcolor;

        if (data.ispublic !== null && data.ispublic !== undefined)
            projectData.ispublic = data.ispublic
        else
            projectData.ispublic = ProjectData.ispublic;

        if (data.allowcomments !== null && data.allowcomments !== undefined)
            projectData.allowcomments = data.allowcomments
        else
            projectData.allowcomments = ProjectData.allowcomments;

        if (data.defaultshapetype)
            projectData.defaultshapetype = data.defaultshapetype
        else
            projectData.defaultshapetype = ProjectData.defaultshapetype;

        if (data.defaultedgetype)
            projectData.defaultedgetype = data.defaultedgetype
        else
            projectData.defaultedgetype = ProjectData.defaultedgetype;

        if (data.defaultconnectiontype)
            projectData.defaultconnectiontype = data.defaultconnectiontype
        else
            projectData.defaultconnectiontype = ProjectData.defaultconnectiontype;

        if (data.themeelementcolor)
            projectData.themeelementcolor = data.themeelementcolor
        else
            projectData.themeelementcolor = ProjectData.themeelementcolor;

        if (data.themebordercolor)
            projectData.themebordercolor = data.themebordercolor
        else
            projectData.themebordercolor = ProjectData.themebordercolor;

        if (data.themeborderwidth)
            projectData.themeborderwidth = data.themeborderwidth
        else
            projectData.themeborderwidth = ProjectData.themeborderwidth;

        if (data.themeedgeshowlabel !== null && data.themeedgeshowlabel !== undefined)
            projectData.themeedgeshowlabel = data.themeedgeshowlabel
        else
            projectData.themeedgeshowlabel = ProjectData.themeedgeshowlabel;

        if (data.themeedgecolor)
            projectData.themeedgecolor = data.themeedgecolor
        else
            projectData.themeedgecolor = ProjectData.themeedgecolor;

        if (data.themetextshowlabel !== null && data.themetextshowlabel !== undefined)
            projectData.themetextshowlabel = data.themetextshowlabel
        else
            projectData.themetextshowlabel = ProjectData.themetextshowlabel;

        if (data.themetextcolor)
            projectData.themetextcolor = data.themetextcolor
        else
            projectData.themetextcolor = ProjectData.themetextcolor;

        if (data.themetextsize)
            projectData.themetextsize = data.themetextsize
        else
            projectData.themetextsize = ProjectData.themetextsize;

        if (data.themetextborderwidth)
            projectData.themetextborderwidth = data.themetextborderwidth
        else
            projectData.themetextborderwidth = ProjectData.themetextborderwidth;

        if (data.themetextbordercolor)
            projectData.themetextbordercolor = data.themetextbordercolor
        else
            projectData.themetextbordercolor = ProjectData.themetextbordercolor;

        if (data.elementsize)
            projectData.elementsize = parseInt(data.elementsize, 10);
        else
            projectData.elementsize = ProjectData.elementsize;

        if (data.elementspacing)
            projectData.elementspacing = parseInt(data.elementspacing, 10);
        else
            projectData.elementspacing = ProjectData.elementspacing;

        if (data.dynamicsize !== null && data.dynamicsize !== undefined)
            projectData.dynamicsize = data.dynamicsize
        else
            projectData.dynamicsize = ProjectData.dynamicsize;

        saveProject();
    }

    const SelectItem = (id) => {
        if (id === 'x') {
            setSelectedItemId(-1);
            setShowElementProperties(false);
            return;
        }

        setSelectedItemId(id);

        let currentElements = elementsRef.current || [];
        let node = currentElements.filter((el) => el.data.id == id);

        if (node.length === 0 && projectData?.elements) {
            node = projectData.elements.filter((el) => el.data.id == id);
        }

        if (node.length !== 0) {
            let a = node[0];
            a = {
                ...node[0],
                data: { ...ElementData, ...node[0].data }
            }

            setSelectedItem(a);
            setSelectedItemType(node[0].group);

            if (!("ontouchstart" in document.documentElement)) {
                //setShowElementProperties(true);
            }
        }

    }

    const getItem = () => {
        return projectData?.nodes?.filter(el => { return el.id === selectedItemId; });
    }

    const startConnection = () => {
        canvasRef.current.ConnectionStart();
    }

    const handleCloseSb = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setNotificationOpen({ state: false, sev: "success", msg: "N/A" });
    };

    /**
     * Import elements from JSON file
     * Wrapper around the import service that provides component-specific dependencies
     * @param {Array} jsonElements - Array of elements to import
     * @param {Object} options - Import options
     * @param {boolean} options.preserveIds - If true, tries to preserve original IDs (may fail if IDs conflict)
     * @returns {Promise<Object>} Result object with success count, error count, and any errors
     */
    const importElementsFromJson = async (jsonElements, options = {}) => {
        return importElementsFromJsonService(
            jsonElements,
            {
                addNodeData,
                projectData,
                elementsRef,
                setNotificationOpen
            },
            options
        );
    };
    // END: Copied unchanged logic

    // --- Responsive container and styles ---
    const containerStyle = {
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        width: "100vw",
        height: "100vh",
        minHeight: 0,
        minWidth: 0,
        position: "relative",
        overflow: "hidden",
        background: isMobile ? "#fafbfc" : undefined,
    };

    const canvasWrapperStyle = isMobile
        ? {
            flex: 1,
            width: "100vw",
            minHeight: 0,
            minWidth: 0,
            overflowX: "auto",
            overflowY: "auto",
            position: "relative",
            marginTop: 56,
        }
        : {
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            position: "relative",
            height: "calc(100vh - 64px)", // Account for Material-UI AppBar Toolbar height (64px) on desktop
            marginTop: 64, // Position below the fixed AppBar
        };

    // --- Error/Loading Handling ---
    if (isError || isProjectNodesError) {
        return <><h1>Error, sorry.</h1><p>Try again later.</p></>;
    }
    if (isLoading || isProjectNodesLoading || !projectIsFullyLoaded) {
        return (<LoadingCircle />);
    }

    // --- Main Render ---
    return (
        <ProjectContext.Provider value={{ projectId, projectData, elements, selectedItem, addNode, addComment, startConnection, addConnection, deleteElement, deleteElementById, deleteElementByIds, deleteElementDialog, SelectItem, updateElement, updateElementImage, importElementsFromJson }}>
            <ThemeProvider theme={defaultTheme}>
                {/* Responsive layout for mobile and desktop */}
                <div style={containerStyle}>
                    <div
                        style={isMobile ? { width: "100vw", position: "fixed", top: 0, left: 0, zIndex: 1300 } : { position: "fixed", top: 0, left: 0, width: "100%", zIndex: 1300 }}
                    >
                        <MyProjectAppBar
                            setProjectDrawerState={setShowProjectDrawer}
                            elements={searchElements}
                            canvasRef={canvasRef}
                            selectedItem={selectedItemId}
                            ispublic={props.isPublic}
                            publicUserInfo={userPublicData}
                            projectData={projectData}
                            isMobile={isMobile}
                        />
                    </div>
                    <div style={canvasWrapperStyle}>
                        <ProjectCanvas
                            ref={canvasRef}
                            elements={elements}
                            style={{
                                ...(isMobile ? {
                                    minHeight: "calc(100vh - 56px)",
                                    width: "100vw",
                                    overflow: "auto",
                                    position: "relative"
                                } : {}),
                            }}
                        />
                    </div>
                    <ProjectDrawer
                        state={showProjectDrawer}
                        setState={setShowProjectDrawer}
                        project={projectData}
                        update={updateProject}
                        canvasRef={canvasRef}
                        variant={isMobile ? "temporary" : "persistent"}
                        anchor={isMobile ? "bottom" : "left"}
                        fullScreen={isMobile}
                    />
                    {/* Delete dialog - mobile: higher zIndex for overlay */}
                    <DeleteElementDialog
                        id={selectedItemId}
                        name={getItem()?.label}
                        handleDelete={deleteElement}
                        open={showDeleteDialog}
                        close={closeDeleteElementDialog}
                        refreshLayout={() => canvasRef.current.setLayout()}
                        fullScreen={isMobile}
                    />
                    <ElementPropertiesDialog
                        item={selectedItem}
                        type={selectedItemType}
                        open={showElementProperties}
                        setOpen={setShowElementProperties}
                        projectId={params.id}
                        fullScreen={isMobile}
                    />
                    {selectedItem && selectedItemId !== -1 && (
                        <ElementInfo
                            item={selectedItem}
                            openEdit={() => setShowElementProperties(true)}
                            canvasRef={canvasRef}
                            ispublic={props.isPublic}
                            fullScreen={isMobile}
                        />
                    )}
                    {/* 
                        The 'sx' prop allows you to write responsive values like { xs: 62, sm: 72 }, 
                        but 'style' does not interpret breakpoints/options this way.
                        To use responsive 'top', use the 'sx' prop (e.g. with MUI's Box) instead of 'style'. 
                    */}
                    {props.isPublic === true && ( 
                        <ProfilMenuInfo userData={userPublicData} projectData={projectData} />
                    )}
                    {props.isPublic === true && false && (
                         <div
                            style={{
                                position: "fixed",
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 1400, // Above most MUI components
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                width: "100%",
                                pointerEvents: "none", // Let clicks through unless on ad
                            }}
                         >
                            <div
                                id="adsense-placeholder"
                                style={{
                                    minHeight: 90,
                                    minWidth: 320,
                                    pointerEvents: "auto", // Enable interaction with the ad
                                    background: "none",
                                    boxShadow: "0 0 12px 0 rgb(0 0 0 / 10%)",
                                    //borderRadius: 8,
                                    //margin: "8px",
                                    backgroundColor: "transparent"
                                }}
                            >
                                <Adsense
                                    client="ca-pub-4805789185012787"
                                    slot="3770393389"
                                    style={{ display: 'block' }}
                                    responsive="fasle"
                                    adTest="on"
                                />
                            </div>
                         </div>
                    )}
                    <Snackbar open={notificationOpen.state} autoHideDuration={6000} onClose={handleCloseSb}>
                        <Alert onClose={handleCloseSb} severity={notificationOpen.sev} sx={{ width: '100%' }}>
                            {notificationOpen.msg}
                        </Alert>
                    </Snackbar>
                </div>
            </ThemeProvider>
        </ProjectContext.Provider>
    );
};